import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useStytchMember } from '@stytch/react/b2b';
import {
  EditorBreadcrumb,
  EditorHeaderBar,
} from '@nimbalyst/collab-bundle/docs-ui';
import type { TeamSummary } from '../api';
import {
  orgAdminPath,
  type AdminSection,
  type ConsoleMode,
} from '../routing';
import { readNavigationExpanded, rememberNavigationExpanded } from '../navigationPreference';
import { DocsTreeProvider, readDocsTreeDocked, rememberDocsTreeDocked } from '../collab/docsTree';
import { isPhoneViewport } from '../phoneViewport';
import { useRequestsInbox } from '../requests/RequestsInboxContext';
import { DecorativeIcon } from '../organization/DecorativeIcon';
import { NEW_TEAM_PATH } from '../organization/CreateTeamPage';
import {
  buildNavGutterItems,
  groupNavGutterItems,
  navGutterHomePath,
} from './navGutterItems';
import { ThemePicker } from './Chrome';
import { DesktopAppMenuItem } from './DesktopAppOffer';
import { isEmbedded } from '../embedded/embedMarker';

/** Declared beside the routes it is derived from; see `consoleModeForRoute`. */
export type { ConsoleMode } from '../routing';

const adminNavItems: Array<[AdminSection, string, string]> = [
  ['overview', 'Overview', 'dashboard'],
  ['billing', 'Billing', 'credit_card'],
  ['members', 'Roles & members', 'group'],
  ['security', 'Security', 'shield'],
  ['danger', 'Danger zone', 'warning'],
];

/**
 * Shared by every floating menu this shell owns. Matches the surface the shared
 * tracker menus paint: bordered card, `--console-shadow-lg`, 6px of inset.
 */
const MENU_SURFACE = 'z-[100] min-w-[230px] rounded-[9px] border border-nim bg-nim p-1.5 text-nim shadow-[var(--console-shadow-lg)]';
/** A row inside one of those menus. */
const MENU_ITEM = 'grid w-full min-h-[34px] cursor-pointer grid-cols-[20px_minmax(0,1fr)_auto] items-center gap-[7px] rounded-[5px] border-none bg-transparent px-[7px] py-[5px] text-left text-nim-muted hover:bg-nim-hover hover:text-nim';

function ProfileMenu({
  expanded,
  team,
  teams,
  onSelectTeam,
  onLogout,
}: {
  expanded: boolean;
  team: TeamSummary;
  teams: TeamSummary[];
  onSelectTeam: (team: TeamSummary) => void;
  onLogout: () => Promise<void>;
}) {
  const { member } = useStytchMember();
  const [open, setOpen] = useState(false);
  const [orgsOpen, setOrgsOpen] = useState(false);
  // Arrow-key rings. The theme row opens a picker of its own with its own ring,
  // so it stays out of this one and is reached by Tab -- nesting it would make
  // ArrowDown mean "next command here" and "next theme there" at the same time.
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [orgActiveIndex, setOrgActiveIndex] = useState<number | null>(null);
  // `HTMLElement` rather than `HTMLButtonElement`: the desktop-app row is a real
  // link, so it stays keyboard-reachable in the ring without being a button that
  // navigates.
  const itemRefs = useRef<Array<HTMLElement | null>>([]);
  const orgItemRefs = useRef<Array<HTMLElement | null>>([]);
  const profile = useFloating({
    open,
    onOpenChange(nextOpen) {
      setOpen(nextOpen);
      if (!nextOpen) setOrgsOpen(false);
    },
    placement: 'right-end',
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip({ padding: 8 }), shift({ padding: 8 })],
  });
  const profileInteractions = useInteractions([
    useClick(profile.context),
    useDismiss(profile.context),
    useRole(profile.context, { role: 'menu' }),
    useListNavigation(profile.context, {
      listRef: itemRefs,
      activeIndex,
      onNavigate: setActiveIndex,
      loop: true,
    }),
  ]);
  const organizations = useFloating({
    open: orgsOpen,
    onOpenChange: setOrgsOpen,
    placement: 'right-start',
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })],
  });
  const organizationInteractions = useInteractions([
    useClick(organizations.context),
    useDismiss(organizations.context),
    useRole(organizations.context, { role: 'menu' }),
    // `nested` gives the submenu the behaviour a submenu is expected to have:
    // ArrowRight on the parent item opens it and moves focus in, ArrowLeft and
    // Escape close it and hand focus back to that item.
    useListNavigation(organizations.context, {
      listRef: orgItemRefs,
      activeIndex: orgActiveIndex,
      onNavigate: setOrgActiveIndex,
      nested: true,
      focusItemOnOpen: true,
      loop: true,
    }),
  ]);
  const name = member?.name || 'Signed-in account';
  const email = member?.email_address || '';
  const initial = (name || email || 'A').slice(0, 1).toUpperCase();

  return (
    <>
      <button
        ref={profile.refs.setReference}
        {...profileInteractions.getReferenceProps()}
        className={`app-profile-button flex h-[38px] w-full cursor-pointer items-center gap-2.5 overflow-hidden whitespace-nowrap rounded-md border-none bg-transparent text-left text-nim-muted hover:bg-nim-hover hover:text-nim ${expanded ? 'px-2' : 'justify-center px-0'}`}
        type="button"
        aria-label="Open profile menu"
        aria-expanded={open}
      >
        <span className="app-profile-avatar grid size-[26px] shrink-0 place-items-center rounded-full bg-nim-primary text-[0.68rem] font-semibold text-nim-on-primary">{initial}</span>
        {expanded && (
          <span className="app-profile-identity min-w-0 leading-tight">
            <strong className="block truncate text-[0.75rem] font-semibold text-nim">{name}</strong>
            <span className="block truncate text-[0.66rem] text-nim-faint">{email}</span>
          </span>
        )}
      </button>
      {open && (
        <FloatingPortal>
          <FloatingFocusManager context={profile.context} modal={false} initialFocus={-1}>
            <div
              ref={profile.refs.setFloating}
              style={profile.floatingStyles}
              {...profileInteractions.getFloatingProps()}
              className={`app-profile-menu ${MENU_SURFACE}`}
            >
              <div className="app-profile-menu-identity border-b border-nim px-2 pb-[9px] pt-1.5">
                <strong className="block text-[0.8rem]">{name}</strong>
                <span className="mt-0.5 block text-[0.7rem] text-nim-muted">{email}</span>
              </div>
              <button
                ref={(node) => {
                  organizations.refs.setReference(node);
                  itemRefs.current[0] = node;
                }}
                className={`app-menu-item ${MENU_ITEM}`}
                type="button"
                aria-haspopup="menu"
                aria-expanded={orgsOpen}
                tabIndex={activeIndex === 0 ? 0 : -1}
                {...profileInteractions.getItemProps(organizationInteractions.getReferenceProps())}
              >
                <DecorativeIcon icon="domain" size={17} />
                <span>Switch organization</span>
                <DecorativeIcon icon="chevron_right" size={17} />
              </button>
              <DesktopAppMenuItem
                ref={(node) => { itemRefs.current[1] = node; }}
                className={`app-menu-item no-underline ${MENU_ITEM}`}
                tabIndex={activeIndex === 1 ? 0 : -1}
                {...profileInteractions.getItemProps()}
              >
                <DecorativeIcon icon="download" size={17} /><span>Get the desktop app</span>
              </DesktopAppMenuItem>
              <div className="app-profile-theme flex items-center justify-between gap-3 px-[7px] py-1.5 text-[0.76rem] text-nim-muted"><span>Theme</span><ThemePicker /></div>
              <button
                ref={(node) => { itemRefs.current[2] = node; }}
                className={`app-menu-item ${MENU_ITEM}`}
                type="button"
                tabIndex={activeIndex === 2 ? 0 : -1}
                {...profileInteractions.getItemProps({ onClick: () => void onLogout() })}
              >
                <DecorativeIcon icon="logout" size={17} /><span>Sign out</span>
              </button>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
      {orgsOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={organizations.context} modal={false} initialFocus={-1}>
            <div
              ref={organizations.refs.setFloating}
              style={organizations.floatingStyles}
              {...organizationInteractions.getFloatingProps()}
              className={`app-organization-menu ${MENU_SURFACE}`}
            >
              <div className="app-menu-caption px-2 pb-1.5 pt-[5px] text-[0.64rem] font-semibold uppercase tracking-[0.08em] text-nim-faint">Your organizations</div>
              {teams.map((candidate, index) => (
                <button
                  className={`app-organization-option flex w-full cursor-pointer items-center gap-2 rounded-[5px] border-none px-2 py-[7px] text-left text-nim hover:bg-nim-hover ${candidate.orgId === team.orgId ? 'bg-nim-selected' : 'bg-transparent'}`}
                  key={candidate.orgId}
                  ref={(node) => { orgItemRefs.current[index] = node; }}
                  type="button"
                  tabIndex={orgActiveIndex === index ? 0 : -1}
                  {...organizationInteractions.getItemProps({
                    onClick: () => {
                      setOrgsOpen(false);
                      setOpen(false);
                      if (candidate.orgId !== team.orgId) onSelectTeam(candidate);
                    },
                  })}
                >
                  <span className="app-organization-avatar grid size-[26px] shrink-0 place-items-center rounded-md bg-nim-primary text-[0.7rem] font-semibold text-nim-on-primary">{candidate.name.slice(0, 1).toUpperCase()}</span>
                  <span className="min-w-0 flex-1">
                    <strong className="block truncate text-[0.78rem]">{candidate.name}</strong>
                    <small className="mt-px block truncate text-[0.66rem] capitalize text-nim-muted">{candidate.role}</small>
                  </span>
                  {candidate.orgId === team.orgId && (
                    <DecorativeIcon icon="check" size={17} className="text-nim-primary" />
                  )}
                </button>
              ))}
              <Link
                className={`app-create-team-option mt-1 border-t border-nim pt-1.5 no-underline ${MENU_ITEM}`}
                ref={(node) => { orgItemRefs.current[teams.length] = node; }}
                to={NEW_TEAM_PATH}
                tabIndex={orgActiveIndex === teams.length ? 0 : -1}
                {...organizationInteractions.getItemProps({
                  onClick: () => {
                    setOrgsOpen(false);
                    setOpen(false);
                  },
                })}
              >
                <DecorativeIcon icon="add" size={17} /><span>Create a team</span>
              </Link>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}

/** The shape a gutter row and the collapse button share. */
function gutterRowClass(expanded: boolean, active: boolean): string {
  return [
    'flex items-center gap-2.5 overflow-hidden whitespace-nowrap rounded-md border-none no-underline',
    expanded ? 'px-2' : 'justify-center px-0',
    active
      ? 'app-gutter-button-active bg-nim-primary text-nim-on-primary'
      : 'bg-transparent text-nim-muted hover:bg-nim-hover hover:text-nim',
  ].join(' ');
}

export function NavigationGutter({
  mode,
  team,
  teams,
  docsProjectId,
  onSelectTeam,
  onLogout,
  drawerOpen = false,
  onNavigate,
  docsTreeShowing = false,
  onDocsReclick,
}: {
  mode: ConsoleMode;
  team: TeamSummary;
  teams: TeamSummary[];
  docsProjectId: string | null;
  onSelectTeam: (team: TeamSummary) => void;
  onLogout: () => Promise<void>;
  /**
   * True while the phone drawer is showing this gutter. It forces labels on:
   * the drawer is 232px wide whatever the stored preference says, and a
   * 232px column of unlabelled icons is worse than either state.
   */
  drawerOpen?: boolean;
  /** Closes the drawer once the reader has gone somewhere. */
  onNavigate?: () => void;
  /** Whether the Shared Docs tree is showing, docked or flown over; drawn on the Docs item. */
  docsTreeShowing?: boolean;
  /**
   * Re-clicking Docs while already in Docs. There is nowhere to navigate to,
   * so the click shows or hides the tree instead.
   */
  onDocsReclick?: () => void;
}) {
  const [expanded, setExpanded] = useState(readNavigationExpanded);
  const { unreadCount: unreadRequests } = useRequestsInbox();
  const setNavigationExpanded = (nextExpanded: boolean) => {
    setExpanded(nextExpanded);
    rememberNavigationExpanded(nextExpanded);
  };
  // Rebuilt per render rather than memoized: it is four objects, and the unread
  // count that feeds it changes on every inbox frame anyway.
  const embedded = isEmbedded();
  const sections = groupNavGutterItems(buildNavGutterItems({
    team,
    projectId: docsProjectId,
    unreadRequests,
    embedded,
  }));
  const showLabels = expanded || drawerOpen;

  return (
    // The expanded width steps down on a narrow shell, and on a phone this
    // whole element becomes an off-canvas drawer. Both overrides are real
    // `@container` at-rules in `styles.css` keyed off these two attributes,
    // because this app's Tailwind pass has no container-query plugin.
    <nav
      className={`app-navigation-gutter flex min-h-0 shrink-0 flex-col overflow-hidden border-r border-nim bg-nim-secondary px-1.5 py-2 transition-[width] duration-150 ${expanded ? 'w-[204px]' : 'w-12'}`}
      data-expanded={expanded ? 'true' : 'false'}
      data-drawer={drawerOpen ? 'open' : 'closed'}
      aria-label="Application navigation"
    >
      <Link
        className="app-gutter-brand flex h-[34px] shrink-0 items-center gap-[9px] overflow-hidden whitespace-nowrap rounded-md px-1.5 font-app text-[0.88rem] font-semibold text-nim no-underline"
        to={navGutterHomePath(team, docsProjectId)}
        aria-label="Nimbalyst home"
      >
        <span
          className="app-gutter-brand-mark grid size-6 shrink-0 place-items-center rounded-md bg-gradient-to-br from-[#382dcf] to-[#6395ff] text-[0.75rem] font-bold text-white"
          aria-hidden="true"
        >
          N
        </span>
        {showLabels && <span>Nimbalyst</span>}
      </Link>
      {sections.map(({ section, items }) => (
        <div className="app-gutter-section mt-2.5 grid gap-1" key={section} data-gutter-section={section}>
          {items.map((item) => {
            const active = mode === item.id;
            const badge = item.badgeCount ?? 0;
            // The one gutter item with a second gesture: re-clicked while
            // active, Docs shows the tree rather than going nowhere.
            const togglesTree = item.id === 'docs' && active && Boolean(onDocsReclick);
            return (
              <Link
                className={`app-gutter-button relative h-9 ${gutterRowClass(expanded, active)}`}
                key={item.id}
                to={item.to}
                title={
                  badge > 0 ? `${item.label} (${badge} waiting)`
                    : togglesTree ? `${item.label} (click again to browse documents)`
                      : item.label
                }
                aria-current={active ? 'page' : undefined}
                aria-expanded={togglesTree ? docsTreeShowing : undefined}
                data-gutter-item={item.id}
                onClick={(event) => {
                  onNavigate?.();
                  if (!togglesTree) return;
                  event.preventDefault();
                  onDocsReclick?.();
                }}
              >
                <DecorativeIcon icon={item.icon} size={20} className="shrink-0" />
                {showLabels && <span className="min-w-0 truncate">{item.label}</span>}
                {togglesTree && showLabels && (
                  <DecorativeIcon
                    icon={docsTreeShowing ? 'left_panel_close' : 'left_panel_open'}
                    size={16}
                    className="app-gutter-tree-hint ml-auto shrink-0 opacity-70"
                  />
                )}
                {badge > 0 && (
                  <span
                    className={
                      expanded
                        ? `app-gutter-count ml-auto min-w-[18px] rounded-full px-[5px] text-center text-[0.62rem] font-bold leading-[17px] ${active ? 'bg-nim-on-primary text-nim-primary' : 'bg-nim-primary text-nim-on-primary'}`
                        // Collapsed, the count corners the icon. Inset rather
                        // than overhanging, because the row clips its overflow.
                        : `app-gutter-count absolute right-0.5 top-0.5 min-w-[15px] rounded-full px-[3px] text-center text-[0.56rem] font-bold leading-[14px] ${active ? 'bg-nim-on-primary text-nim-primary' : 'bg-nim-primary text-nim-on-primary'}`
                    }
                    aria-label={`${badge} ${item.label.toLowerCase()} waiting on you`}
                  >
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      ))}
      <div className="app-gutter-bottom mt-auto grid gap-1 border-t border-nim pt-2">
        <button
          className={`app-gutter-collapse h-[30px] w-full cursor-pointer text-[0.75rem] ${gutterRowClass(expanded, false)}`}
          type="button"
          title={expanded ? 'Collapse navigation' : 'Expand navigation'}
          aria-label={expanded ? 'Collapse navigation' : 'Expand navigation'}
          onClick={() => setNavigationExpanded(!expanded)}
        >
          <DecorativeIcon icon={expanded ? 'chevron_left' : 'chevron_right'} size={20} className="shrink-0" />
          {showLabels && <span>Collapse</span>}
        </button>
        {/* The phone app owns sign-out, org switching and the desktop offer. */}
        {!embedded && <ProfileMenu expanded={expanded} team={team} teams={teams} onSelectTeam={onSelectTeam} onLogout={onLogout} />}
      </div>
    </nav>
  );
}

function OrganizationMode({ team, children }: { team: TeamSummary; children: ReactNode }) {
  const location = useLocation();
  const activeSection = adminNavItems.find(([section]) => (
    location.pathname === orgAdminPath(team, section)
  ));
  return (
    <div className="organization-mode grid h-full min-h-0 overflow-hidden">
      <aside className="organization-subnav flex min-h-0 flex-col overflow-hidden border-r border-nim bg-nim-secondary">
        <header className="organization-subnav-header grid shrink-0 gap-0.5 border-b border-nim px-3 pb-[11px] pt-[13px]">
          <strong className="text-[0.88rem]">Organization</strong>
          <span className="text-[0.68rem] text-nim-muted">Settings and access</span>
        </header>
        <nav className="grid min-h-0 gap-0.5 overflow-y-auto px-1.5 py-2" aria-label="Organization sections">
          {adminNavItems.map(([section, label, icon]) => (
            <NavLink
              className={({ isActive }) => [
                'organization-subnav-item flex h-8 items-center gap-2 rounded-[5px] px-[9px] text-[0.8rem] no-underline',
                section === 'danger' ? 'text-nim-error' : 'text-nim-muted',
                isActive
                  ? 'bg-nim-selected font-semibold text-nim'
                  : 'hover:bg-nim-hover hover:text-nim',
              ].join(' ')}
              end
              key={section}
              to={orgAdminPath(team, section)}
            >
              <DecorativeIcon icon={icon} size={17} /><span>{label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>
      <section className="organization-content flex min-w-0 min-h-0 flex-col overflow-hidden">
        <EditorHeaderBar
          breadcrumb={(
            <EditorBreadcrumb
              crumbs={[
                { id: 'team', label: team.name },
                { id: 'organization', label: 'Organization', current: !activeSection },
                ...(activeSection ? [{ id: 'section', label: activeSection[1], current: true }] : []),
              ]}
            />
          )}
        />
        <div className="organization-content-scroll min-h-0 flex-1 overflow-y-auto p-[clamp(1.25rem,3vw,2.5rem)]">{children}</div>
      </section>
    </div>
  );
}

export function AppShell({
  mode,
  team,
  teams,
  docsProjectId,
  onSelectTeam,
  onLogout,
  notice,
  children,
}: {
  mode: ConsoleMode;
  team: TeamSummary;
  teams: TeamSummary[];
  docsProjectId: string | null;
  onSelectTeam: (team: TeamSummary) => void;
  onLogout: () => Promise<void>;
  notice?: ReactNode;
  children: ReactNode;
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Held here rather than in the docs shell: the gutter that shows it is a
  // sibling of that shell, not a descendant. The docked pane is a remembered
  // preference; the fly-over is not, and leaving Docs puts it away so a return
  // to Docs does not open onto the tree the reader last left up. On a phone
  // the docked pane is an overlay over the document, and a cold link names a
  // document, so the preference does not apply there.
  const [docsTreeDocked, setDocsTreeDocked] = useState(() => readDocsTreeDocked() && !isPhoneViewport());
  const [docsTreeFlyoverOpen, setDocsTreeFlyoverOpen] = useState(false);
  const dockDocsTree = (docked: boolean) => {
    setDocsTreeDocked(docked);
    setDocsTreeFlyoverOpen(false);
    rememberDocsTreeDocked(docked);
  };
  useEffect(() => {
    if (mode !== 'docs') setDocsTreeFlyoverOpen(false);
  }, [mode]);
  const docsTreeShowing = docsTreeDocked || docsTreeFlyoverOpen;
  // The registry is the only thing that knows what a mode is called, so the
  // phone bar's caption is read back out of it rather than restated. The unread
  // count does not affect a label, so zero is honest here.
  const modeLabel = buildNavGutterItems({ team, projectId: docsProjectId, unreadRequests: 0 })
    .find((item) => item.id === mode)?.label ?? team.name;
  return (
    <DocsTreeProvider
      value={{
        docked: docsTreeDocked,
        setDocked: dockDocsTree,
        flyoverOpen: docsTreeFlyoverOpen,
        setFlyoverOpen: setDocsTreeFlyoverOpen,
      }}
    >
      {/* `container-name`/`container-type` stay in `styles.css`: this app's
          Tailwind pass has no container-query plugin, so the `@container`
          at-rules keyed off this element are authored as real CSS. */}
      <div
        className="console-app-shell flex h-[100dvh] min-h-0 w-full overflow-hidden bg-nim"
        data-mode={mode}
        data-nav-drawer={drawerOpen ? 'open' : 'closed'}
        data-docs-tree={docsTreeDocked ? 'docked' : docsTreeFlyoverOpen ? 'flyover' : 'closed'}
      >
        <NavigationGutter
          mode={mode}
          team={team}
          teams={teams}
          docsProjectId={docsProjectId}
          onSelectTeam={onSelectTeam}
          onLogout={onLogout}
          drawerOpen={drawerOpen}
          onNavigate={() => setDrawerOpen(false)}
          docsTreeShowing={docsTreeShowing}
          // Show or hide: a docked pane is hidden, otherwise the fly-over
          // toggles. Docking is the breadcrumb's job, not the gutter's.
          onDocsReclick={() => {
            if (docsTreeDocked) dockDocsTree(false);
            else setDocsTreeFlyoverOpen(!docsTreeFlyoverOpen);
          }}
        />
        {/* Always rendered, never displayed above the phone breakpoint. A
            JS-measured breakpoint would need its own resize observer and would
            disagree with the `@container` rules beside it for a frame. */}
        <button
          type="button"
          className="console-nav-scrim fixed inset-0 z-[80] hidden border-none bg-[color-mix(in_srgb,black_45%,transparent)]"
          aria-label="Close navigation"
          tabIndex={drawerOpen ? 0 : -1}
          onClick={() => setDrawerOpen(false)}
        />
        <main className="console-mode-surface min-h-0 min-w-0 flex-1 overflow-hidden">
          <div className="console-mode-stack flex h-full min-h-0 flex-col overflow-hidden">
            <div className="console-mobile-bar hidden shrink-0 items-center gap-2 border-b border-nim bg-nim px-2 py-1.5">
              <button
                type="button"
                className="console-nav-toggle grid size-8 cursor-pointer place-items-center rounded-md border-none bg-transparent text-nim-muted hover:bg-nim-hover hover:text-nim"
                aria-label="Open navigation"
                aria-expanded={drawerOpen}
                onClick={() => setDrawerOpen(true)}
              >
                <DecorativeIcon icon="menu" size={20} />
              </button>
              <span className="min-w-0 flex-1 truncate text-[0.82rem] font-semibold text-nim">{modeLabel}</span>
            </div>
            {notice && <div className="console-shell-notice shrink-0 border-b border-nim bg-nim px-3 py-2">{notice}</div>}
            <div className="console-mode-content min-h-0 min-w-0 flex-1 overflow-hidden [&>*:only-child]:h-full [&>*:only-child]:min-h-0">
              {mode === 'organization' ? <OrganizationMode team={team}>{children}</OrganizationMode> : children}
            </div>
          </div>
        </main>
      </div>
    </DocsTreeProvider>
  );
}
